/**
 * "Match fonts" — which Figma family stands in for each font the document used.
 *
 * pdf-to-figma has offered this on its editable tab for as long as it has had one, and this
 * app could not: nothing knew which fonts a file contained until the converter had already
 * read its text, by which point the import was underway. The probe reads the names first.
 *
 * Only on the editable path. Vector mode draws glyphs as outlines and image mode rasterises
 * them, so in neither does a font name survive for anything to be matched against — asking
 * there would be a control that changes nothing.
 */

import { Box, Disclosure, Flex, Select, Text } from '@figma-importers/ui';
import { useImportStore } from '../store/importStore';

/** Left to the matcher, which is what happens when no mapping is set. */
const AUTO = '';

export function FontMatching() {
  const probe = useImportStore((s) => s.probe);
  const available = useImportStore((s) => s.availableFonts);
  const mappings = useImportStore((s) => s.fontMappings);
  const setFontMapping = useImportStore((s) => s.setFontMapping);

  const fonts = probe?.fonts ?? [];
  if (fonts.length === 0) return null;

  /*
   * "Auto" first, then the families Figma has. Before the sandbox answers `get-fonts` the
   * list is just that one entry, which is honest: the import will match automatically, and
   * the control says so rather than looking broken.
   */
  const options = [
    // Short, because the trigger is narrow: "Match automatically" renders as
    // "Match…", which says less than the one word does.
    { value: AUTO, label: 'Auto' },
    ...available.map((family) => ({ value: family, label: family })),
  ];

  return (
    <Disclosure label="Match fonts" count={fonts.length}>
      <Flex direction="column" gap="1.5">
        {fonts.map((font) => (
          <Flex key={font} align="center" gap="2">
            <Text
              fontSize="2xs"
              color="figma.textSecondary"
              flex="1"
              minW="0"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title={font}
            >
              {font}
            </Text>
            <Box w="140px" flexShrink={0}>
              <Select
                value={mappings[font] ?? AUTO}
                onChange={(v) => setFontMapping(font, v === AUTO ? null : v)}
                options={options}
                ariaLabel={`Figma font for ${font}`}
              />
            </Box>
          </Flex>
        ))}
      </Flex>
    </Disclosure>
  );
}
